import { toast } from "sonner";
import { ApiError } from "@/lib/api";

interface ErrorContext {
  /** Logical action that failed, e.g. "upload-task-evidence". Helps find log lines. */
  action?: string;
  /** Anything else relevant — taskId, projectId, payAppId, etc. */
  extra?: Record<string, unknown>;
}

interface DebugBundle {
  timestamp: string;
  url: string;
  userAgent: string;
  action: string | null;
  message: string;
  status: number | null;
  requestId: string | null;
  apiPath: string | null;
  apiMethod: string | null;
  responseBody: unknown;
  stack: string | null;
  extra: Record<string, unknown> | null;
}

const RING_SIZE = 20;

function getRing(): DebugBundle[] {
  if (typeof window === "undefined") return [];
  const w = window as unknown as { __pbosErrors?: DebugBundle[] };
  if (!w.__pbosErrors) w.__pbosErrors = [];
  return w.__pbosErrors;
}

function buildBundle(error: unknown, context?: ErrorContext): DebugBundle {
  const isApi = error instanceof ApiError;
  const isErr = error instanceof Error;
  return {
    timestamp: new Date().toISOString(),
    url: typeof window !== "undefined" ? window.location.href : "",
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    action: context?.action ?? null,
    message: isErr ? error.message : String(error),
    status: isApi ? error.status : null,
    requestId: isApi ? error.requestId : null,
    apiPath: isApi ? error.path : null,
    apiMethod: isApi ? error.method : null,
    responseBody: isApi ? error.body : null,
    stack: isErr ? (error.stack ?? null) : null,
    extra: context?.extra ?? null,
  };
}

function formatBundle(b: DebugBundle): string {
  return `PBOS error report
─────────────────
when:        ${b.timestamp}
action:      ${b.action ?? "—"}
url:         ${b.url}
api:         ${b.apiMethod ?? "—"} ${b.apiPath ?? "—"}
status:      ${b.status ?? "—"}
requestId:   ${b.requestId ?? "—"}
message:     ${b.message}

userAgent:   ${b.userAgent}

extra:
${JSON.stringify(b.extra ?? {}, null, 2)}

response body:
${JSON.stringify(b.responseBody ?? null, null, 2)}

stack:
${b.stack ?? "(none)"}
`;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  } catch {
    return false;
  }
}

/**
 * Show an error toast with a "Copy debug info" action. The copied bundle
 * includes timestamp, URL, action label, server requestId, status, response
 * body, and stack — everything needed to correlate with CloudWatch logs.
 *
 * Every error is also pushed onto a ring buffer at `window.__pbosErrors` so
 * `copyLastError()` can retrieve the most recent one even if the toast was
 * dismissed.
 */
export function toastError(error: unknown, context?: ErrorContext): void {
  ensureWindowHelper();
  const bundle = buildBundle(error, context);
  const ring = getRing();
  ring.push(bundle);
  if (ring.length > RING_SIZE) ring.shift();

  const shortId = bundle.requestId ? bundle.requestId.slice(0, 8) : null;
  const description = shortId ? `request ${shortId} • click "Copy debug info"` : undefined;

  toast.error(bundle.message, {
    description,
    duration: 10_000,
    action: {
      label: "Copy debug info",
      onClick: async () => {
        const ok = await copyToClipboard(formatBundle(bundle));
        if (ok) toast.success("Debug info copied to clipboard");
        else toast.error("Couldn't copy — open DevTools and run copyLastError()");
      },
    },
  });
}

/**
 * Console helper: returns the formatted text for the last error and copies it
 * to the clipboard. Use from DevTools when a toast was dismissed too quickly.
 */
async function copyLastError(): Promise<string | null> {
  const ring = getRing();
  const last = ring[ring.length - 1];
  if (!last) {
    // biome would warn on console.log per code-quality.md; warn is allowed
    console.warn("No errors recorded yet.");
    return null;
  }
  const text = formatBundle(last);
  await copyToClipboard(text);
  return text;
}

let windowHelperRegistered = false;
function ensureWindowHelper(): void {
  if (windowHelperRegistered || typeof window === "undefined") return;
  (window as unknown as { copyLastError?: typeof copyLastError }).copyLastError = copyLastError;
  windowHelperRegistered = true;
}
